import type { Router, RouteRecordRaw } from 'vue-router';
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
} from 'vue-router';
import { Capacitor } from '@capacitor/core';

import { routesMain, officesSubdomainRoutes, sharedRoutes } from './routes';
import { mobileRoutes } from './routes.mobile';
import { useUser } from 'stores/user/useUser';
import { captureMessage } from '@sentry/vue';
import { dynamicLoadingErrorMessages } from 'src/router/dynamicLoadingErrorMessages';
import { OpenAPI, ReservationsApi } from 'src/api';
import { defineRouter } from '@quasar/app-vite/wrappers';
import { detectTenantSlug } from 'src/utils/domainUtils';

const checkOfficeExistenceBySlug = async (): Promise<boolean> => {
  const subdomainUrl = detectTenantSlug();
  if (!subdomainUrl) return false;

  try {
    OpenAPI.BASE = process.env.BASE_API_URL;

    await ReservationsApi.reservationsGetCompanyBasedOnSlugGet({
      slug: subdomainUrl,
    });
    return true;
  } catch {
    return false;
  }
};

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export let globalRouterInstance: Router | undefined = undefined;

export default defineRouter(async function (/* { store, ssrContext } */) {
  const isMobile =
    Capacitor.isNativePlatform() || process.env.FORCE_MOBILE === 'true';

  let routes: RouteRecordRaw[];
  if (isMobile) {
    routes = mobileRoutes;
  } else {
    const isSubdomainValid = await checkOfficeExistenceBySlug();
    routes = isSubdomainValid ? officesSubdomainRoutes : routesMain;
    routes.push(...sharedRoutes);
  }

  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
      ? createWebHistory
      : createWebHashHistory;

  const routerInstance = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE),
  });

  routerInstance.beforeEach(async (to, from, next) => {
    const store = useUser();
    store.setCurrentFacilitySlug(detectTenantSlug());

    // if (
    //   from.meta.patientPortal !=
    //   to.matched.some((route) => route.meta.patientPortal)
    // ) {
    //   await store.logout();
    // }

    if (!store.loggedIn) {
      if (to.matched.some((route) => route.meta.public)) {
        return next();
      }
      return next({ name: 'login' });
    }
    if (to.matched.some((route) => route.meta.patientPortal)) {
      if (!store.patientUser) {
        await store.getPatientUser();
      }
      if (to.matched.some((route) => route.name === 'login')) {
        return next({ name: 'patient-user-visits' });
      }
      return next();
    }

    if (!store.user && !to.meta.public) {
      await store.getUser();
    }
    // Redirect already-logged-in users away from the auth/login screens.
    // Web uses the route named 'auth'; mobile uses its own 'login' screen.
    // Both platforms land on the shared 'home' route after login.
    const authRouteName = isMobile ? 'login' : 'auth';
    const homeRouteName = 'home';
    if (to.matched.some((route) => route.name === authRouteName)) {
      return next({ name: homeRouteName });
    }
    return next();
  });

  routerInstance.onError((error: Error, to) => {
    if (
      process.env.ENVIRONMENT !== 'localhost' &&
      dynamicLoadingErrorMessages.some((errorMessage) =>
        error.message.toLowerCase().includes(errorMessage.toLowerCase())
      )
    ) {
      captureMessage(
        'Triggered automatic reload after failed to fetch dynamically imported module.',
        {
          extra: {
            message: error.message,
          },
        }
      );
      window.location.href = to.fullPath;
    }
  });
  globalRouterInstance = routerInstance;
  return routerInstance;
});
